import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import _ from 'lodash'

import { redirectToNewPage } from '../../../global/helpers/global-helpers'
import { removeEmptyWidgets } from "../../../digital-sales-room/helpers/dsr-draft-helpers";

import { Box, Flex } from '@chakra-ui/react'
import DsrGenericErrorModal from '../../components/DsrGenericErrorModal';
import DsrProspectHeader from '../DsrProspectHeader';
import DsrDynamicModal from '../../messages/DsrDynamicModal';
import RenderProspectDraftContent from './RenderProspectDraftContent';
import PageLoader from '../../../global/components/helper-components/components/PageLoader';
import LaunchdeckButton from '../../../global/components/helper-components/components/LaunchdeckButton';
import RenderProspectPreviewContent from "../../dsr-draft/RenderProspectPreviewContent";
import useLaunchdeckModal from '../../../global/hooks/use-launchdeck-modal';
import DsrLiveEditProspectHeader from '../DsrLiveEditProspectHeader';


export const ProspectEditDraftPage = ({
  isLiveEdit,
  updatePdsr,
  getProspectDsr,
  dsrPagesRedux,
  dsrStatusRedux,
  showDsrModalRedux,
  userActionRequiredRedux,
  showDsrModalLoaderRedux,
  dsrTitleRedux,
  resetDsrTitle,
  resetDsrSelectedDisplaySubpage,
  resetDsrPages,
  updatePdsrReset,
  dsrInvalidRequestRedux,
  isMobileView,
  isShrunkHeader
}) => {
  const { pDsrId } = useParams()
  const initRef = useRef(false);
  const { openLaunchdeckModal, closeLaunchdeckModal } = useLaunchdeckModal();

  let [pdsrPagesLocalState, setPdsrPagesLocalState] = useState(null)
  let [dsrHasUnsavedChanges, setDsrHasUnsavedChanges] = useState(false)
  let [pageLoadingState, setPageLoadingState] = useState(true)
  let [isPagePreviewMode, setIsPagePreviewMode] = useState(false)
  let [modalTransferPayload, setModalTransferPayload] = useState('')
  const clearedPdsrPagesLocalState = removeEmptyWidgets(pdsrPagesLocalState)

  const headerMenuHeight = isShrunkHeader ? '100px' : isMobileView ? '160px' : '140px';

  useEffect(() => {
    if (!initRef.current && pDsrId !== 'new') {
      document.title = 'Launchdeck Digital Sales Room'
      document.description = 'Create customised private digital sales rooms that let Sales & Purchasing teams agree on deals quickly'

      getProspectDsr({ pDsrId, status: 'draft', isLiveEdit })
      initRef.current = true
    }
  }, [pDsrId, getProspectDsr])


  useEffect(() => {
    if (((!pDsrId) || (dsrInvalidRequestRedux === true)) && initRef.current) {
      redirectToNewPage('/invalid')
    }
  }, [pDsrId, dsrInvalidRequestRedux])


  useEffect(() => {
    if (dsrPagesRedux) {
      setPdsrPagesLocalState(_.cloneDeep(dsrPagesRedux))
      setPageLoadingState(false)
    }

  }, [dsrPagesRedux])


  useEffect(() => {
    // Review if this is still needed with autosave
    if (pdsrPagesLocalState) setDsrHasUnsavedChanges(!_.isEqual(pdsrPagesLocalState, dsrPagesRedux))
  }, [dsrPagesRedux, pdsrPagesLocalState, setDsrHasUnsavedChanges])

  useEffect(() => {
    if (pDsrId && dsrHasUnsavedChanges) {
      updatePdsr({
        pDsrId: pDsrId,
        dsrPages: pdsrPagesLocalState,
        sourceEvent: "save-draft-prospect-pdsr"
      })
    }

  }, [pDsrId, dsrHasUnsavedChanges, pdsrPagesLocalState])


  const handleDsrModalClose = () => {
    closeLaunchdeckModal()
  }

  const handleChangeUserActionRequired = (newUserActionRequired, modalTransferPayload) => {
    setModalTransferPayload(modalTransferPayload)
    openLaunchdeckModal(newUserActionRequired)
  }

  const renderProspectorHeader = () => {
    if (isLiveEdit) {
      return (
        <DsrLiveEditProspectHeader
          pDsrId={pDsrId}
          dsrTitle={dsrTitleRedux ? dsrTitleRedux : "Edit Prospector"}
          updatePdsr={updatePdsr}
          pdsrPagesLocalState={pdsrPagesLocalState}
          handlePublishDsr={handlePublishDsr}
          dsrStatus={dsrStatusRedux}
          isPagePreviewMode={isPagePreviewMode}
          setIsPagePreviewMode={setIsPagePreviewMode}
          handleChangeUserActionRequired={handleChangeUserActionRequired}
          isLiveEdit={isLiveEdit}
        />
      )
    } else {
      return (
        <DsrProspectHeader
          pDsrId={pDsrId}
          dsrTitle={dsrTitleRedux ? dsrTitleRedux : "A New Prospect Room"}
          updatePdsr={updatePdsr}
          pdsrPagesLocalState={pdsrPagesLocalState}
          handlePublishDsr={handlePublishDsr}
          dsrStatus={dsrStatusRedux}
          isPagePreviewMode={isPagePreviewMode}
          setIsPagePreviewMode={setIsPagePreviewMode}
          handleChangeUserActionRequired={handleChangeUserActionRequired}
          isLiveEdit={isLiveEdit}
        />
      )
    }
  }


  // PDSR status Management
  const handlePublishDsr = () => {
    if (dsrStatusRedux === 'draft') {
      openLaunchdeckModal('publish-prospect-dsr')
    }

    if (dsrStatusRedux === 'reverted-draft') {
      openLaunchdeckModal('re-publish-prospect-dsr')
    }
  }

  const handlerSetDsrPages = (pages) => {
    setPdsrPagesLocalState(pages)
  }

  const handlerAddDsrPage = (newDsrPage, sourcePageId) => {
    if (!sourcePageId) {
      // Adds brand new page
      setPdsrPagesLocalState((prevState) => [...prevState, newDsrPage])
    } else {
      // Puts duplicated page right below source page
      const sourcePageIndex = pdsrPagesLocalState?.findIndex((page) => page.pageId === sourcePageId)
      if (sourcePageIndex >= 0) {
        const newPagesArray = [
          ...pdsrPagesLocalState.slice(0, sourcePageIndex + 1),
          newDsrPage, // Insert the new object
          ...pdsrPagesLocalState.slice(sourcePageIndex + 1),
        ];

        setPdsrPagesLocalState(newPagesArray)
      }
    }
  }

  const handlerRemoveDsrPage = (dsrPageId) => {
    setPdsrPagesLocalState((prevState) => prevState.filter((page) => page.pageId !== dsrPageId))
  }

  useEffect(() => {
    return () => resetDsrTitle() && resetDsrSelectedDisplaySubpage() && resetDsrPages() && updatePdsrReset()
  }, [resetDsrTitle, resetDsrSelectedDisplaySubpage, resetDsrPages, updatePdsrReset])

  if (isMobileView) {
    return (
      <Flex margin='-2% -7% -7% -7%' height='100vh' justifyContent='center' alignItems='center' background='white' flexDirection='column'>
        <Flex mb='0.625em' px='2em' justify='center' textAlign='center'>
          For the best experience editing your content, we recommend using a computer's wider display
        </Flex>
        <Flex direction='column' mt='1em' justify='center' alignItems='center'>
          <Flex justify={['center', 'flex-end', 'flex-end', 'flex-end']} w='100%'>
            <LaunchdeckButton bg='brand.500' color='white' onClick={() => window.location.replace('/home')} label='Edit on a computer later' />
          </Flex>
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex direction="column" height='100%'>
      <Flex direction="row" flexGrow="1">

        <Flex w="100%" direction="column" alignItems="center" bg="white">
          {pageLoadingState ? <PageLoader /> :
            <>

              <Box width='100%' position='fixed' zIndex='2'>
                {renderProspectorHeader()}
              </Box>


              <Flex w="100%" className="authentication-container" pt={headerMenuHeight}
                transition="padding 0.3s ease-in-out"
                justify="center"
              >
                {isPagePreviewMode
                  ? (
                    <RenderProspectPreviewContent
                      dsrId={pDsrId}
                      pagesLocalState={pdsrPagesLocalState}
                    />
                  )
                  : (
                    <RenderProspectDraftContent
                      pagesLocalState={pdsrPagesLocalState}
                      replaceDsrPages={handlerSetDsrPages}
                      addDsrPage={handlerAddDsrPage}
                      removeDsrPage={handlerRemoveDsrPage}
                      headerMenuHeight={headerMenuHeight}
                    />
                  )
                }
                <DsrGenericErrorModal dsrId={pDsrId} />

                <DsrDynamicModal
                  pDsrId={pDsrId}
                  userActionRequired={userActionRequiredRedux}
                  showDsrModal={showDsrModalRedux}
                  showDsrModalLoader={showDsrModalLoaderRedux}
                  handleDsrModalClose={handleDsrModalClose}
                  dsrPages={clearedPdsrPagesLocalState}
                  modalTransferPayload={modalTransferPayload}
                />

              </Flex>
            </>
          }
        </Flex>
      </Flex>
    </Flex>
  )
}
